/**
 * 判断文本是否为「完整 HTML 文档」（可独立渲染）。
 * Markdown 中夹杂 HTML、或仅 ```html 代码块，不算完整 HTML。
 */
export function isCompleteHtmlDocument(text) {
  const s = String(text || '').trim();
  if (!s) return false;
  // 含 Markdown 围栏 → 视为 md，不算纯 HTML 页
  if (/```/.test(s)) return false;
  // 须以文档声明或 <html> 开头，并以 </html> 收尾
  if (/^<!DOCTYPE\s+html[\s>]/i.test(s) && /<\/html>\s*$/i.test(s)) return true;
  if (/^<html[\s>]/i.test(s) && /<\/html>\s*$/i.test(s)) return true;
  return false;
}

/**
 * 从文本中截取「从 DOCTYPE/html 到最后一个 </html>」的完整文档。
 * 使用最后一个 </html>，避免脚本字符串里较早出现的 </html> 导致截断。
 */
export function extractHtmlSpan(text) {
  const s = String(text || '');
  if (!s) return '';

  const doctypeIdx = s.search(/<!DOCTYPE\s+html/i);
  const htmlIdx = s.search(/<html[\s>]/i);
  let start = -1;
  if (doctypeIdx >= 0) start = doctypeIdx;
  else if (htmlIdx >= 0) start = htmlIdx;
  if (start < 0) return '';

  const fromStart = s.slice(start);
  let lastEnd = -1;
  const re = /<\/html>/gi;
  let m;
  while ((m = re.exec(fromStart)) !== null) {
    lastEnd = m.index + m[0].length;
  }
  if (lastEnd < 0) return '';

  const extracted = fromStart.slice(0, lastEnd).trim();
  return isCompleteHtmlDocument(extracted) ? extracted : '';
}

/**
 * 从模型输出中尽量提取纯 HTML 文档（去掉前后说明、代码围栏）。
 * 若无法提取则返回 trim 后的原文。
 */
export function extractPureHtmlDocument(text) {
  const raw = String(text || '').trim();
  if (!raw) return '';
  if (isCompleteHtmlDocument(raw)) return raw;

  // 1) 优先：```html 开栏后，按 DOCTYPE/html → 最后一个 </html> 截取
  //    （不依赖闭合 ```，避免 HTML 内出现 ``` 时被非贪婪正则截断）
  const htmlFenceRe = /```html[^\n]*\n?/gi;
  let fenceOpen;
  let best = '';
  while ((fenceOpen = htmlFenceRe.exec(raw)) !== null) {
    const after = raw.slice(fenceOpen.index + fenceOpen[0].length);
    const span = extractHtmlSpan(after);
    if (span && span.length > best.length) best = span;
  }
  if (best) return best;

  // 2) 通用围栏：对每个代码块尝试提取，取最长完整文档
  const anyFenceRe = /```(?:[a-zA-Z0-9_-]*)?[^\n]*\n?([\s\S]*?)```/g;
  let m;
  while ((m = anyFenceRe.exec(raw)) !== null) {
    const code = String(m[1] || '').trim();
    const span = extractHtmlSpan(code) || (isCompleteHtmlDocument(code) ? code : '');
    if (span && span.length > best.length) best = span;
  }
  if (best) return best;

  // 3) 无围栏：整段里找完整 HTML
  const fromWhole = extractHtmlSpan(raw);
  if (fromWhole) return fromWhole;

  return raw;
}
